import React, { useEffect, useRef, useState } from "react";
import { getFirestore, collection, getDocs, doc, deleteDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { FiEdit2, FiTrash2, FiX, FiSearch, FiRotateCcw, FiBookOpen, FiAlertTriangle } from "react-icons/fi";

// ── helpers ──────────────────────────────────────────────────────
function getAmharicAndLength(translation) {
    if (!translation) return { amharic: "", length: 0 };
    const amharic = translation.split("(")[0].trim();
    return { amharic, length: amharic.length };
}

function IconBtn({ icon, color, border, onClick, title }) {
    const ref = useRef(null);
    return (
        <button
            ref={ref}
            title={title}
            onClick={onClick}
            onMouseDown={() => { if (ref.current) { ref.current.style.transform = "translateY(2px)"; ref.current.style.borderBottomWidth = "2px"; } }}
            onMouseUp={() => { if (ref.current) { ref.current.style.transform = ""; ref.current.style.borderBottomWidth = "3px"; } }}
            onMouseLeave={() => { if (ref.current) { ref.current.style.transform = ""; ref.current.style.borderBottomWidth = "3px"; } }}
            style={{
                width: 34, height: 34, borderRadius: 10,
                border: `1.5px solid ${border}`, borderBottomWidth: 3,
                background: color, color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", flexShrink: 0,
                transition: "transform 80ms ease, border-bottom-width 80ms ease",
            }}
        >
            {icon}
        </button>
    );
}

// ── Word Bank Modal ───────────────────────────────────────────────
function WordBankModal({ flashcards, onClose }) {
    const [wordSearch, setWordSearch] = useState("");
    const [sortByLength, setSortByLength] = useState(false);
    const [filterSet, setFilterSet] = useState("");
    const searchRef = useRef(null);

    useEffect(() => {
        searchRef.current?.focus();
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);

    const allWords = [];
    flashcards.forEach(flashcard => {
        if (Array.isArray(flashcard.words)) {
            flashcard.words.forEach(wordObj => {
                if (wordObj.word) {
                    const { amharic, length } = getAmharicAndLength(wordObj.translation || "");
                    allWords.push({
                        word: wordObj.word,
                        translation: wordObj.translation || "",
                        amharic,
                        amharicLength: length,
                        setTitle: flashcard.title || "Untitled",
                        setId: flashcard.id,
                    });
                }
            });
        }
    });

    let filtered = allWords.filter(w =>
        (w.word.toLowerCase().includes(wordSearch.toLowerCase()) ||
         w.amharic.toLowerCase().includes(wordSearch.toLowerCase()) ||
         w.translation.toLowerCase().includes(wordSearch.toLowerCase())) &&
        (filterSet ? w.setId === filterSet : true)
    );
    if (sortByLength) {
        filtered = [...filtered].sort((a, b) => a.amharicLength - b.amharicLength);
    } else {
        filtered = [...filtered].sort((a, b) => a.word.localeCompare(b.word));
    }

    const hasFilters = wordSearch || filterSet || sortByLength;

    return (
        <div
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            style={{
                position: "fixed", inset: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 1000,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-end",
                fontFamily: "'Quicksand', sans-serif",
            }}
        >
            <div style={{
                background: "#f7f9fb",
                width: "min(680px, 95vw)",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "-4px 0 32px rgba(0,0,0,0.14)",
                animation: "slideIn 0.22s ease",
            }}>
                <style>{`@keyframes slideIn { from { transform: translateX(60px); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>

                {/* ── Header ── */}
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "22px 28px",
                    background: "#2a9d8f", color: "#fff",
                    flexShrink: 0,
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <FiBookOpen size={22} />
                        <div>
                            <div style={{ fontWeight: 800, fontSize: 18, lineHeight: 1 }}>Word Bank</div>
                            <div style={{ fontSize: 12, fontWeight: 600, opacity: 0.85, marginTop: 3 }}>
                                {allWords.length.toLocaleString()} word{allWords.length !== 1 ? "s" : ""} across {flashcards.length} set{flashcards.length !== 1 ? "s" : ""}
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: "rgba(255,255,255,0.2)", border: "none",
                            width: 34, height: 34, borderRadius: 10,
                            color: "#fff", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "background 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.35)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.2)"; }}
                    >
                        <FiX size={18} />
                    </button>
                </div>

                {/* ── Filter bar ── */}
                <div style={{
                    display: "flex", gap: 10, padding: "16px 28px",
                    background: "#fff", borderBottom: "1px solid #E0E0E0",
                    flexShrink: 0, flexWrap: "wrap",
                }}>
                    {/* Search */}
                    <div style={{ position: "relative", flex: "1 1 180px", minWidth: 160 }}>
                        <FiSearch size={14} style={{
                            position: "absolute", left: 11, top: "50%",
                            transform: "translateY(-50%)", color: "#aaa", pointerEvents: "none",
                        }} />
                        <input
                            ref={searchRef}
                            type="text"
                            placeholder="Search words…"
                            value={wordSearch}
                            onChange={e => setWordSearch(e.target.value)}
                            style={{
                                fontFamily: "'Quicksand', sans-serif", fontSize: 13, fontWeight: 600,
                                padding: "8px 12px 8px 32px", borderRadius: 10,
                                border: "1.5px solid #E0E0E0", background: "#f7f9fb",
                                width: "100%", outline: "none", color: "#000",
                            }}
                        />
                    </div>

                    {/* Set filter */}
                    <select
                        value={filterSet}
                        onChange={e => setFilterSet(e.target.value)}
                        style={{
                            fontFamily: "'Quicksand', sans-serif", fontSize: 13, fontWeight: 600,
                            padding: "8px 12px", borderRadius: 10,
                            border: "1.5px solid #E0E0E0", background: "#f7f9fb",
                            color: "#000", cursor: "pointer", outline: "none",
                            flex: "1 1 140px",
                        }}
                    >
                        <option value="">All Sets</option>
                        {flashcards.map(f => (
                            <option key={f.id} value={f.id}>{f.title || "Untitled"}</option>
                        ))}
                    </select>

                    {/* Sort by length toggle */}
                    <button
                        onClick={() => setSortByLength(s => !s)}
                        style={{
                            fontFamily: "'Quicksand', sans-serif", fontSize: 12, fontWeight: 700,
                            padding: "8px 14px", borderRadius: 10, cursor: "pointer",
                            border: `1.5px solid ${sortByLength ? "#2a9d8f" : "#E0E0E0"}`,
                            background: sortByLength ? "#2a9d8f" : "#f7f9fb",
                            color: sortByLength ? "#fff" : "#606060",
                            transition: "all 0.15s",
                            whiteSpace: "nowrap",
                        }}
                    >
                        Sort by Length
                    </button>

                    {/* Reset */}
                    {hasFilters && (
                        <button
                            onClick={() => { setWordSearch(""); setFilterSet(""); setSortByLength(false); }}
                            style={{
                                fontFamily: "'Quicksand', sans-serif", fontSize: 12, fontWeight: 700,
                                padding: "8px 12px", borderRadius: 10, cursor: "pointer",
                                border: "1.5px solid #E0E0E0", background: "#fff",
                                color: "#e76f51", display: "flex", alignItems: "center", gap: 5,
                            }}
                        >
                            <FiRotateCcw size={12} /> Reset
                        </button>
                    )}
                </div>

                {/* ── Result count ── */}
                <div style={{
                    padding: "8px 28px", fontSize: 12, fontWeight: 700,
                    color: "#606060", borderBottom: "1px solid #E0E0E0",
                    background: "#fff", flexShrink: 0,
                }}>
                    {filtered.length.toLocaleString()} result{filtered.length !== 1 ? "s" : ""}
                    {(wordSearch || filterSet) && ` for "${wordSearch || ""}"${filterSet ? ` in "${flashcards.find(f => f.id === filterSet)?.title || ""}"` : ""}`}
                </div>

                {/* ── Table ── */}
                <div style={{ flex: 1, overflow: "auto" }}>
                    {filtered.length === 0 ? (
                        <div style={{
                            display: "flex", flexDirection: "column", alignItems: "center",
                            justifyContent: "center", height: 200, gap: 10,
                            color: "#aaa", fontWeight: 600, fontSize: 14,
                        }}>
                            <FiSearch size={28} />
                            No words match your search.
                        </div>
                    ) : (
                        <table style={{ width: "100%", minWidth: 420, borderCollapse: "collapse", fontFamily: "'Quicksand', sans-serif" }}>
                            <thead>
                                <tr style={{ background: "#2a9d8f", color: "#fff", position: "sticky", top: 0, zIndex: 1 }}>
                                    {["Word", "Amharic", "Length", "Flashcard Set"].map(col => (
                                        <th key={col} style={{
                                            textAlign: "left", fontWeight: 700,
                                            fontSize: 11, textTransform: "uppercase",
                                            letterSpacing: "0.08em",
                                            padding: "11px 16px",
                                        }}>
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((w, i) => (
                                    <tr
                                        key={w.word + w.setId + i}
                                        style={{
                                            background: i % 2 === 0 ? "#fff" : "#f7f9fb",
                                            borderBottom: "1px solid #f0f0f0",
                                        }}
                                    >
                                        <td style={{ padding: "10px 16px", fontWeight: 700, fontSize: 14 }}>
                                            {w.word}
                                        </td>
                                        <td style={{ padding: "10px 16px", fontSize: 14 }}>
                                            {w.amharic || (
                                                <span style={{ color: "#ccc" }}>—</span>
                                            )}
                                        </td>
                                        <td style={{ padding: "10px 16px" }}>
                                            {w.amharicLength > 0 ? (
                                                <span style={{
                                                    display: "inline-block",
                                                    background: "#2a9d8f18", color: "#2a9d8f",
                                                    border: "1px solid #2a9d8f40",
                                                    borderRadius: 999, padding: "2px 10px",
                                                    fontSize: 12, fontWeight: 700,
                                                }}>
                                                    {w.amharicLength}
                                                </span>
                                            ) : (
                                                <span style={{ color: "#ccc" }}>—</span>
                                            )}
                                        </td>
                                        <td style={{ padding: "10px 16px", fontSize: 13, color: "#606060", fontWeight: 600 }}>
                                            {w.setTitle}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Flashcards page ───────────────────────────────────────────────
function Flashcards() {
    const [flashcards, setFlashcards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showWordBank, setShowWordBank] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const db = getFirestore();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchFlashcards = async () => {
            const snap = await getDocs(collection(db, "flashcards"));
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            list.sort((a, b) => (a.title || "").toLowerCase().localeCompare((b.title || "").toLowerCase()));
            setFlashcards(list);
            setLoading(false);
        };
        fetchFlashcards();
    }, [db]);

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this flashcard set?")) return;
        setDeleteError("");
        try {
            await deleteDoc(doc(db, "flashcards", id));
            setFlashcards(prev => prev.filter(f => f.id !== id));
        } catch (err) {
            setDeleteError(err?.code === "permission-denied"
                ? "Permission denied. Your CMS admin account needs delete access to flashcards."
                : `Delete failed: ${err.message}`);
        }
    };

    const btn = (bg, border) => ({
        backgroundColor: bg, color: "#fff", borderColor: border,
        fontSize: 14, padding: "9px 20px",
        display: "inline-flex", alignItems: "center", gap: 7,
    });

    if (loading) {
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", gap: 12, color: "#606060", fontWeight: 600, fontFamily: "'Quicksand', sans-serif" }}>
                <div style={{ width: 20, height: 20, border: "2.5px solid #E0E0E0", borderTop: "2.5px solid #2a9d8f", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                Loading flashcards…
            </div>
        );
    }

    const totalWords = flashcards.reduce((sum, f) => sum + (Array.isArray(f.words) ? f.words.length : 0), 0);

    return (
        <div style={{ fontFamily: "'Quicksand', sans-serif", maxWidth: 1100 }}>

            {/* ── Page header ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
                <div>
                    <h1 style={{ margin: 0 }}>Flashcard Sets</h1>
                    <div style={{ color: "#606060", fontWeight: 600, fontSize: 14, marginTop: 4 }}>
                        {flashcards.length} set{flashcards.length !== 1 ? "s" : ""} · {totalWords.toLocaleString()} word{totalWords !== 1 ? "s" : ""}
                    </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                    <button className="primary-button" onClick={() => setShowWordBank(true)} style={btn("#264653", "#1b2e36")}>
                        <FiBookOpen size={15} /> Word Bank
                    </button>
                    <button className="primary-button" onClick={() => navigate("/addflashcards")} style={btn("#2a9d8f", "#258D80")}>
                        + Add Set
                    </button>
                </div>
            </div>

            {deleteError && (
                <div style={{ color: "#e76f51", fontSize: 13, fontWeight: 600, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
                    <FiAlertTriangle size={14} /> {deleteError}
                </div>
            )}

            {/* ── Flashcard grid ── */}
            {flashcards.length === 0 ? (
                <div style={{
                    background: "#fff", borderRadius: 20, border: "1.5px dashed #E0E0E0",
                    padding: "48px 32px", textAlign: "center",
                    color: "#aaa", fontWeight: 600, fontSize: 15,
                }}>
                    No flashcard sets yet.{" "}
                    <button onClick={() => navigate("/addflashcards")} style={{ color: "#2a9d8f", background: "none", border: "none", fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "'Quicksand', sans-serif" }}>
                        Create one →
                    </button>
                </div>
            ) : (
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 16,
                }}>
                    {flashcards.map(flashcard => {
                        const wordCount = Array.isArray(flashcard.words) ? flashcard.words.length : 0;
                        return (
                            <div key={flashcard.id} style={{
                                background: "#fff", borderRadius: 18,
                                border: "1.5px solid #E0E0E0", borderBottom: "3px solid #E0E0E0",
                                padding: "20px 20px 16px",
                                display: "flex", flexDirection: "column", gap: 14,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                            }}>
                                {/* Card header */}
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6, lineHeight: 1.3 }}>
                                        {flashcard.title || "Untitled"}
                                    </div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <span style={{
                                            background: "#2a9d8f18", color: "#2a9d8f",
                                            border: "1px solid #2a9d8f30", borderRadius: 999,
                                            fontSize: 11, fontWeight: 700, padding: "2px 10px",
                                        }}>
                                            {wordCount} word{wordCount !== 1 ? "s" : ""}
                                        </span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div style={{ display: "flex", gap: 8, borderTop: "1px solid #f3f3f3", paddingTop: 12 }}>
                                    <IconBtn
                                        icon={<FiEdit2 size={15} />}
                                        color="#e9c46a" border="#d4b15f"
                                        title="Edit"
                                        onClick={() => navigate(`/flashcards/${flashcard.id}/edit`)}
                                    />
                                    <IconBtn
                                        icon={<FiTrash2 size={15} />}
                                        color="#e76f51" border="#cf6348"
                                        title="Delete"
                                        onClick={() => handleDelete(flashcard.id)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showWordBank && (
                <WordBankModal flashcards={flashcards} onClose={() => setShowWordBank(false)} />
            )}
        </div>
    );
}

export default Flashcards;
